// app/components/SymbolSearch/SymbolSearch.tsx
'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { getInstruments } from '../Connectors/groww/instruments/downloadInstruments'
import { getSecureItem } from '../Connectors/utils/secureStorage'

// Define the GrowwInstrument type based on CSV format
interface GrowwInstrument {
  trading_symbol: string;
  name: string;
  segment: string;
  exchange: string;
  series?: string;
}

export interface SymbolSearchModalProps {
  onSelect: (symbol: string) => void
  onClose: () => void
  customPopularSymbols?: string[]
  title?: string
  recentStorageKey?: string
  maxRecentSymbols?: number
  className?: string
}

/**
 * A reusable symbol search modal component that can be used across the application
 * Features:
 * - Search functionality for symbols
 * - Recent symbols tracking with localStorage
 * - Customizable popular symbols list
 * - Customizable styling
 */
export const SymbolSearchModal: React.FC<SymbolSearchModalProps> = ({
  onSelect,
  onClose,
  customPopularSymbols,
  title = 'Search Symbol',
  recentStorageKey = 'recentSymbols',
  maxRecentSymbols = 5,
  className = '',
}) => {
  // Search query state
  const [query, setQuery] = useState('')
  
  // Recent symbols from localStorage
  const [recentSymbols, setRecentSymbols] = useState<string[]>([])
  
  // Groww instruments state
  const [growwInstruments, setGrowwInstruments] = useState<GrowwInstrument[]>([])
  const [isGrowwEnabled, setIsGrowwEnabled] = useState(false)
  const [growwSearchResults, setGrowwSearchResults] = useState<GrowwInstrument[]>([])
  
  // Loading states
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  
  // Create an index for faster searching
  const instrumentsIndex = useRef<Record<string, GrowwInstrument[]>>({})
  
  // Default popular symbols if not provided
  const popular = customPopularSymbols || [
    'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'PAYTM.NS', 'YESBANK.NS', 
    'RELIANCE.NS', 'HDFCBANK.NS', 'TCS.NS', 'ICICIBANK.NS', 'AXISBANK.NS'
  ]

  // Create index for faster searches
  const buildSearchIndex = (instruments: GrowwInstrument[]) => {
    const index: Record<string, GrowwInstrument[]> = {}
    
    // Create index by first letter for quick lookups
    instruments.forEach(instrument => {
      const symbol = instrument.trading_symbol || ''
      if (symbol) {
        const firstChar = symbol.charAt(0).toLowerCase()
        if (!index[firstChar]) {
          index[firstChar] = []
        }
        index[firstChar].push(instrument)
      }
      
      const name = instrument.name || ''
      if (name) {
        const firstChar = name.charAt(0).toLowerCase()
        if (!index[firstChar]) {
          index[firstChar] = []
        }
        // Only add if not already added by symbol
        if (!index[firstChar].find(i => i === instrument)) {
          index[firstChar].push(instrument)
        }
      }
    })
    
    return index
  }

  // Check if Groww connector is enabled
  useEffect(() => {
    const checkGrowwEnabled = () => {
      try {
        const connectors = getSecureItem('broker_enabled')
        if (connectors) {
          // The broker_enabled value is just the broker ID string, not an array
          return connectors === 'groww'
        }
      } catch (error) {
        console.error('Error checking Groww connector:', error)
      }
      return false
    }
    
    const isEnabled = checkGrowwEnabled()
    setIsGrowwEnabled(isEnabled)
    
    if (isEnabled) {
      setIsLoadingInstruments(true)
      console.log('Groww connector is enabled, loading instruments...')
      // Load Groww instruments
      getInstruments('CASH').then(instruments => {
        console.log(`Loaded ${instruments.length} Groww instruments`)
        if (instruments.length > 0) {
          console.log('Sample instrument:', instruments[0])
        }
        setGrowwInstruments(instruments)
        
        // Build search index
        instrumentsIndex.current = buildSearchIndex(instruments)
        console.log('Search index built for fast lookups')
      }).catch(error => {
        console.error('Error loading Groww instruments:', error)
      }).finally(() => {
        setIsLoadingInstruments(false)
      })
    }
  }, [])

  // Load recent symbols from localStorage on component mount
  useEffect(() => {
    const stored = localStorage.getItem(recentStorageKey)
    if (stored) {
      try {
        setRecentSymbols(JSON.parse(stored))
      } catch (error) {
        console.error('Error parsing stored symbols:', error)
        setRecentSymbols([])
      }
    }
  }, [recentStorageKey])

  // Fast search function using index
  const searchInstruments = (searchTerm: string) => {
    setIsSearching(true)
    
    try {
      if (!searchTerm.trim()) {
        setGrowwSearchResults([])
        return
      }
      
      const term = searchTerm.trim().toLowerCase()
      
      // For very short queries, use the index for faster lookup
      if (term.length <= 2) {
        const firstChar = term.charAt(0)
        const indexedInstruments = instrumentsIndex.current[firstChar] || []
        
        const results = indexedInstruments
          .filter(instrument => {
            const symbol = (instrument.trading_symbol || '').toLowerCase()
            const name = (instrument.name || '').toLowerCase()
            return symbol.includes(term) || name.includes(term)
          })
          .slice(0, 10)
          
        setGrowwSearchResults(results)
        return
      }
      
      // For longer queries, search all instruments
      const results = growwInstruments
        .filter(instrument => {
          const symbol = (instrument.trading_symbol || '').toLowerCase()
          const name = (instrument.name || '').toLowerCase()
          return symbol.includes(term) || name.includes(term)
        })
        .slice(0, 10)
        
      setGrowwSearchResults(results)
    } finally {
      setIsSearching(false)
    }
  }

  // Optimized search in Groww instruments when query changes
  useEffect(() => {
    // Use a slight delay for typing to avoid searching on every keystroke
    const handler = setTimeout(() => {
      // Always search in Groww instruments when connector is enabled
      if (isGrowwEnabled && growwInstruments.length > 0 && query.trim()) {
        searchInstruments(query)
      } else {
        setGrowwSearchResults([])
      }
    }, 100) // Small delay for better performance
    
    return () => clearTimeout(handler)
  }, [query, growwInstruments, isGrowwEnabled])

  // Filter function for search - memoized for performance
  const filteredRecent = useMemo(() => {
    return recentSymbols.filter(s => 
      s.toLowerCase().includes(query.trim().toLowerCase())
    )
  }, [recentSymbols, query])
  
  const filteredPopular = useMemo(() => {
    return popular.filter(s => 
      s.toLowerCase().includes(query.trim().toLowerCase())
    )
  }, [popular, query])

  // Format symbol for navigation based on exchange
  const formatSymbol = (instrument: GrowwInstrument): string => {
    const exchange = instrument.exchange || ''
    if (exchange.toUpperCase() === 'NSE') {
      return `${instrument.trading_symbol}.NS`
    } else if (exchange.toUpperCase() === 'BSE') {
      return `${instrument.trading_symbol}.BS`
    }
    return instrument.trading_symbol
  }

  // Handle symbol selection with recent tracking
  const handleSelect = (symbol: string, instrument?: GrowwInstrument) => {
    // If instrument is provided, format the symbol
    const selectedSymbol = instrument ? formatSymbol(instrument) : symbol
    
    // Save to recents in localStorage
    const stored = localStorage.getItem(recentStorageKey) || '[]'
    const recents = JSON.parse(stored) as string[]
    const updated = [selectedSymbol, ...recents.filter((s) => s !== selectedSymbol)].slice(0, maxRecentSymbols)
    localStorage.setItem(recentStorageKey, JSON.stringify(updated))
    
    // Call the provided onSelect callback
    onSelect(selectedSymbol)
  }

  // Use a ref to track if component is mounted for client-side portal rendering
  const [mounted, setMounted] = useState(false)
  
  // Set mounted state to true after component mounts
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])
  
  // Check if we have any results
  const hasRegularResults = filteredRecent.length > 0 || filteredPopular.length > 0
  const hasGrowwResults = growwSearchResults.length > 0
  const hasNoResults = query.trim() && !hasRegularResults && !hasGrowwResults

  // Modal content that will be rendered in the portal
  const modalContent = (
    <div
      className="fixed inset-0 bg-[#88a1ac]/50 flex items-center justify-center z-[9999]"
      onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className={`bg-[#88a1ac] text-white rounded-lg w-[50%] p-4 ${className}`}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 10000 }}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="relative mb-4 bg-white rounded-lg p-2">
          <input
            type="text"
            placeholder="Search symbol or company"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim() && 
                  !hasRegularResults && !hasGrowwResults) {
                handleSelect(query.trim())
              }
            }}
            className="w-full p-2 rounded bg-white placeholder-gray-400 text-gray-800 pr-12"
            autoFocus
          />
          {isSearching && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-[#88a1ac] border-t-transparent rounded-full"></div>
            </div>
          )}
          {hasNoResults && !isSearching && (
            <button
              onClick={() => handleSelect(query.trim())}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#88a1ac] text-white px-2 py-1 rounded hover:bg-[#7a919b] text-sm"
            >
              Enter
            </button>
          )}
        </div>
        
        {/* Initial loading state */}
        {isGrowwEnabled && isLoadingInstruments && !query && (
          <div className="flex items-center justify-center p-4 bg-[#ffffff11] rounded">
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            <span>Syncing instruments...</span>
          </div>
        )}

        {/* Always show Recent section */}
        <div className="font-semibold mb-1">Recent</div>
        {filteredRecent.map((sym) => (
          <div
            key={sym}
            onClick={() => handleSelect(sym)}
            className="p-2 hover:bg-[#ffffff33] cursor-pointer rounded"
          >
            {sym}
          </div>
        ))}
        {recentSymbols.length === 0 && (
          <div className="text-gray-300 mb-2">No recents yet</div>
        )}

        {/* Always show Popular section */}
        <div className="font-semibold mt-4 mb-1">Popular</div>
        {filteredPopular.map((sym) => (
          <div
            key={sym}
            onClick={() => handleSelect(sym)}
            className="p-2 hover:bg-[#ffffff33] cursor-pointer rounded"
          >
            {sym}
          </div>
        ))}

        {/* Always show Groww Instruments Results if enabled and has results */}
        {isGrowwEnabled && growwSearchResults.length > 0 && (
          <>
            <div className="font-semibold mt-4 mb-1">Groww Instruments</div>
            {growwSearchResults.map((instrument) => (
              <div
                key={`${instrument.exchange || 'exchange'}_${instrument.trading_symbol || 'symbol'}`}
                onClick={() => handleSelect(instrument.trading_symbol || '', instrument)}
                className="p-2 hover:bg-[#ffffff33] cursor-pointer rounded"
              >
                <div className="font-medium">{(instrument.trading_symbol || '').toUpperCase()}</div>
                <div className="text-xs text-gray-200">
                  {instrument.name || ''} | {(instrument.segment || '').toUpperCase()} | {(instrument.exchange || '').toUpperCase()}
                </div>
              </div>
            ))}
          </>
        )}
        
        {hasNoResults && !isSearching && (
          <div className="mt-4 p-3 bg-[#ffffff22] rounded text-center">
            <p>No matching symbols found</p>
            <button
              onClick={() => handleSelect(query.trim())}
              className="mt-2 bg-white text-[#88a1ac] px-4 py-1 rounded hover:bg-gray-100 font-medium"
            >
              Use "{query.trim()}"
            </button>
          </div>
        )}
      </div>
    </div>
  )
  
  // Only render the portal on the client-side after component has mounted
  return mounted ? createPortal(modalContent, document.body) : null
}

/**
 * A hook to manage symbol search functionality
 * Returns state and handlers for using the SymbolSearchModal
 */
export const useSymbolSearch = (initialSymbol: string = '') => {
  const [showModal, setShowModal] = useState(false)
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol)

  const openSymbolSearch = () => setShowModal(true)
  const closeSymbolSearch = () => setShowModal(false)
  
  const handleSymbolSelect = (newSymbol: string) => {
    setCurrentSymbol(newSymbol)
    closeSymbolSearch()
    return newSymbol
  }

  return {
    showModal,
    currentSymbol,
    setCurrentSymbol,
    openSymbolSearch,
    closeSymbolSearch,
    handleSymbolSelect
  }
}

/**
 * A button component that opens the symbol search modal
 */
export interface SymbolButtonProps {
  symbol: string
  onClick: () => void
  className?: string
}

export const SymbolButton: React.FC<SymbolButtonProps> = ({ 
  symbol, 
  onClick,
  className = ''
}) => {
  return (
    <div
      onClick={onClick}
      className={`flex items-center bg-[#88a1ac]/20 text-white rounded-full px-4 py-2 cursor-pointer 
        transition-all duration-300 ease-in-out
        shadow-[inset_-2px_-2px_5px_rgba(136,161,172,0.1),inset_2px_2px_5px_rgba(0,0,0,0.3)]
        hover:shadow-[inset_2px_2px_5px_rgba(136,161,172,0.1),inset_-2px_-2px_5px_rgba(0,0,0,0.3)]
        hover:bg-[#88a1ac]/30 ${className}`}
    >
      <div className="w-6 h-6 bg-[#88a1ac]/25 rounded-full flex items-center justify-center mr-3
        shadow-[inset_-2px_-2px_5px_rgba(136,161,172,0.1),inset_2px_2px_5px_rgba(0,0,0,0.3)]">
        <span className="text-sm font-bold text-[#88a1ac]">{symbol.charAt(0).toUpperCase()}</span>
      </div>
      <span className="font-medium text-[#88a1ac]">{symbol.toUpperCase()}</span>
    </div>
  )
}

// Default export for convenience
export default SymbolSearchModal